import { useState } from "react";
import { Phone, MessageCircle, Mail, MapPin, CheckCircle2, Send } from "lucide-react";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20need%20consultation%20for%20tax%20services%20in%20Coimbatore.%20Please%20assist%20me";
const PHONE_URL = "tel:+918940988776";

const CONTACT = {
  phone: "+91 89409 88776",
  email: "wintrustsolutions@gmail.com",
  address: "Coimbatore, Tamil Nadu",
};

const SERVICES = [
  "GST Registration",
  "GST Return Filing",
  "Income Tax Return Filing",
  "TDS Filing",
  "Business Registration",
  "Health Insurance Advisory",
  "Business Loan Assistance",
  "Personal Loan Assistance",
  "Other",
];

const ContactForm = () => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("");
  const [requirements, setRequirements] = useState("");
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Full name is required";
    if (!phone.trim()) newErrors.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(phone.trim())) newErrors.phone = "Enter a valid 10-digit phone number";
    if (!service) newErrors.service = "Please select a service";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const url =
      `https://wa.me/918940988776?text=` +
      `Hi%20WinTrust%20Solutions%2C%0A%0A` +
      `Name%3A%20${encodeURIComponent(name.trim())}%0A` +
      `Phone%3A%20%2B91${encodeURIComponent(phone.trim())}%0A` +
      `Service%20Needed%3A%20${encodeURIComponent(service)}%0A` +
      `Requirement%3A%20${encodeURIComponent(requirements.trim() || "—")}`;

    window.open(url, "_blank", "noopener,noreferrer");
    setSubmitted(true);
  };

  return (
    <section id="contact" className="bg-navy section-padding">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left column */}
          <div className="text-white">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4 font-heading">
              Need Professional Tax Support in Coimbatore?
            </h2>
            <p className="text-slate-300 mb-8 text-lg">
              Get expert tax support and stay fully compliant without stress. Our experts are ready to help you with GST, ITR, TDS, business registration, loans, and health insurance advisory. Serving businesses across Coimbatore & Tamil Nadu.
            </p>

            <address className="space-y-4 mb-8 not-italic">
              <a
                id="phone-contact"
                href={PHONE_URL}
                data-location="contact"
                className="flex items-center gap-3 text-white hover:text-gold transition-colors"
              >
                <Phone size={20} />
                {CONTACT.phone}
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                className="flex items-center gap-3 text-white hover:text-gold transition-colors"
              >
                <Mail size={20} />
                {CONTACT.email}
              </a>
              <div className="flex items-start gap-3 text-slate-300">
                <MapPin size={20} className="flex-shrink-0 mt-1" />
                {CONTACT.address}
              </div>
            </address>

            <div className="flex gap-4">
              <a
                id="whatsapp-final"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-location="final-cta"
                className="btn-whatsapp"
              >
                <MessageCircle size={18} />
                WhatsApp Us
              </a>
              <a
                id="phone-cta"
                href={PHONE_URL}
                data-location="final-cta"
                className="btn-primary flex items-center gap-2"
              >
                <Phone size={18} />
                Call Now
              </a>
            </div>
          </div>

          {/* Right column - Custom Form */}
          <div
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 md:p-8 shadow-lg"
            data-testid="contact-form-container"
          >
            <h3 className="text-2xl font-semibold text-white mb-6 font-heading" data-testid="contact-form-title">
              Talk to a Tax Expert
            </h3>

            {submitted ? (
              <div
                className="bg-green-500/10 border border-green-400/30 rounded-lg p-5 flex items-start gap-3"
                data-testid="contact-form-success"
              >
                <CheckCircle2 className="text-green-400 flex-shrink-0 mt-0.5" size={22} />
                <div>
                  <p className="text-white font-semibold mb-1">Redirecting you to WhatsApp...</p>
                  <p className="text-slate-300 text-sm">We will respond within 2 hours.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form" noValidate>
                <div>
                  <label className="block text-white text-sm font-medium mb-2" htmlFor="cf-name">
                    Full Name <span className="text-gold">*</span>
                  </label>
                  <input
                    id="cf-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:border-gold focus:bg-white/15 transition-colors"
                    placeholder="Your full name"
                    data-testid="contact-name-input"
                  />
                  {errors.name && <p className="text-red-300 text-xs mt-1" data-testid="contact-name-error">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2" htmlFor="cf-phone">
                    Phone Number <span className="text-gold">*</span>
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 bg-white/10 border border-white/20 border-r-0 rounded-l-lg text-slate-300 text-sm font-medium">
                      +91
                    </span>
                    <input
                      id="cf-phone"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                      className="flex-1 bg-white/10 border border-white/20 rounded-r-lg px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:border-gold focus:bg-white/15 transition-colors"
                      placeholder="10-digit number"
                      data-testid="contact-phone-input"
                    />
                  </div>
                  {errors.phone && <p className="text-red-300 text-xs mt-1" data-testid="contact-phone-error">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2" htmlFor="cf-service">
                    Select Service <span className="text-gold">*</span>
                  </label>
                  <select
                    id="cf-service"
                    value={service}
                    onChange={(e) => setService(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-gold focus:bg-white/15 transition-colors appearance-none"
                    data-testid="contact-service-select"
                  >
                    <option value="" className="text-navy">Choose a service…</option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s} className="text-navy">{s}</option>
                    ))}
                  </select>
                  {errors.service && <p className="text-red-300 text-xs mt-1" data-testid="contact-service-error">{errors.service}</p>}
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2" htmlFor="cf-requirements">
                    Your Requirements
                  </label>
                  <textarea
                    id="cf-requirements"
                    rows={4}
                    value={requirements}
                    onChange={(e) => setRequirements(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:border-gold focus:bg-white/15 transition-colors resize-none"
                    placeholder="Briefly describe your requirement..."
                    data-testid="contact-requirements-input"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-gold hover:bg-gold/90 text-navy font-semibold py-3.5 rounded-lg flex items-center justify-center gap-2 transition-colors shadow-md"
                  data-testid="contact-submit-btn"
                >
                  <Send size={18} />
                  Send WhatsApp Message
                </button>

                <p className="text-slate-400 text-xs text-center">
                  By submitting, you'll be redirected to WhatsApp with your details prefilled.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
